import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Calendar, Users, GraduationCap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Attendance() {
  const { user } = useAuth();
  const [studentRecords, setStudentRecords] = useState([]);
  const [teacherRecords, setTeacherRecords] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchDate, setSearchDate] = useState('');
  const [activeType, setActiveType] = useState('students');

  useEffect(() => {
    fetchAttendance();
    if (user.role !== 'student' && user.role !== 'teacher') {
      fetchReports();
    }
  }, []);

  const fetchAttendance = async () => {
    try {
      if (user.role === 'student' || user.role === 'teacher') {
        // Students and teachers only see their own records
        const { data } = await axios.get(`${BACKEND_URL}/api/attendance`, {
          withCredentials: true,
        });
        if (user.role === 'student') {
          setStudentRecords(data);
        } else {
          setTeacherRecords(data);
        }
      } else {
        // Admin sees both
        const [studentData, teacherData] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/attendance?user_type=student`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/attendance?user_type=teacher`, { withCredentials: true }),
        ]);
        setStudentRecords(studentData.data);
        setTeacherRecords(teacherData.data);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/attendance/reports`, {
        withCredentials: true,
      });
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const filteredStudentRecords = searchDate
    ? studentRecords.filter((r) => r.date === searchDate)
    : studentRecords;

  const filteredTeacherRecords = searchDate
    ? teacherRecords.filter((r) => r.date === searchDate)
    : teacherRecords;

  if (loading) {
    return <div>Loading...</div>;
  }

  // For students/teachers, show only their own records
  if (user.role === 'student') {
    return (
      <div data-testid="attendance-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Attendance
          </h1>
          <p className="text-[#64748B] mt-2">View your attendance records</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Attendance Records</CardTitle>
                <CardDescription>Your attendance history</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#64748B]" />
                <Input
                  type="date"
                  data-testid="date-filter-input"
                  placeholder="Filter by date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  className="w-48"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredStudentRecords.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">
                No attendance records found
              </div>
            ) : (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Marked By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudentRecords.map((record) => (
                      <TableRow key={record.id} className="table-row" data-testid="attendance-row">
                        <TableCell className="font-medium">{record.date}</TableCell>
                        <TableCell>{record.class_name}</TableCell>
                        <TableCell>{record.subject || '-'}</TableCell>
                        <TableCell>
                          <span
                            className={`badge ${
                              record.status === 'present'
                                ? 'badge-present'
                                : 'badge-absent'
                            }`}
                            data-testid={`status-${record.status}`}
                          >
                            {record.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-[#64748B]">{record.marked_by}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (user.role === 'teacher') {
    return (
      <div data-testid="attendance-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Attendance
          </h1>
          <p className="text-[#64748B] mt-2">View your attendance records</p>
        </div>

        <Tabs defaultValue="records" className="space-y-6">
          <TabsList>
            <TabsTrigger value="records" data-testid="tab-records">Records</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="records">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>My Attendance Records</CardTitle>
                    <CardDescription>Your attendance history</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#64748B]" />
                    <Input
                      type="date"
                      data-testid="date-filter-input"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      className="w-48"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredTeacherRecords.length === 0 ? (
                  <div className="text-center py-12 text-[#64748B]">
                    No attendance records found
                  </div>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Department</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Marked By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTeacherRecords.map((record) => (
                          <TableRow key={record.id} className="table-row" data-testid="attendance-row">
                            <TableCell className="font-medium">{record.date}</TableCell>
                            <TableCell>{record.department || record.class_name || '-'}</TableCell>
                            <TableCell>
                              <span
                                className={`badge ${
                                  record.status === 'present'
                                    ? 'badge-present'
                                    : 'badge-absent'
                                }`}
                              >
                                {record.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-[#64748B]">{record.marked_by}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {reports && (
            <TabsContent value="reports">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-[#64748B]">Total Records</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                      {reports.teacher_stats.total}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-[#64748B]">Present</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'Manrope' }}>
                      {reports.teacher_stats.present}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-[#64748B]">Absent</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#F59E0B]" style={{ fontFamily: 'Manrope' }}>
                      {reports.teacher_stats.absent}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    );
  }

  // Admin view with separate tabs for students and teachers
  return (
    <div data-testid="attendance-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Attendance
        </h1>
        <p className="text-[#64748B] mt-2">View and manage attendance records</p>
      </div>

      <Tabs defaultValue="students" onValueChange={setActiveType} className="space-y-6">
        <TabsList>
          <TabsTrigger value="students" data-testid="tab-students">
            <GraduationCap className="w-4 h-4 mr-2" />
            Students
          </TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">
            <Users className="w-4 h-4 mr-2" />
            Teachers
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Student Attendance Records</CardTitle>
                  <CardDescription>View all student attendance</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#64748B]" />
                  <Input
                    type="date"
                    data-testid="date-filter-input"
                    placeholder="Filter by date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="w-48"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredStudentRecords.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  No student attendance records found
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Marked By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStudentRecords.map((record) => (
                        <TableRow key={record.id} className="table-row" data-testid="attendance-row">
                          <TableCell className="font-medium">{record.date}</TableCell>
                          <TableCell>{record.user_name}</TableCell>
                          <TableCell>{record.class_name}</TableCell>
                          <TableCell>{record.subject || '-'}</TableCell>
                          <TableCell>
                            <span
                              className={`badge ${
                                record.status === 'present'
                                  ? 'badge-present'
                                  : 'badge-absent'
                              }`}
                              data-testid={`status-${record.status}`}
                            >
                              {record.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-[#64748B]">{record.marked_by}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teachers">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Teacher Attendance Records</CardTitle>
                  <CardDescription>View all teacher attendance</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#64748B]" />
                  <Input
                    type="date"
                    data-testid="date-filter-input-teachers"
                    placeholder="Filter by date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="w-48"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredTeacherRecords.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  No teacher attendance records found
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Marked By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTeacherRecords.map((record) => (
                        <TableRow key={record.id} className="table-row" data-testid="teacher-attendance-row">
                          <TableCell className="font-medium">{record.date}</TableCell>
                          <TableCell>{record.user_name}</TableCell>
                          <TableCell>{record.department || '-'}</TableCell>
                          <TableCell>
                            <span
                              className={`badge ${
                                record.status === 'present'
                                  ? 'badge-present'
                                  : 'badge-absent'
                              }`}
                            >
                              {record.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-[#64748B]">{record.marked_by}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {reports && (
          <TabsContent value="reports">
            <div className="space-y-6">
              {/* Student Stats */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Student Attendance Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Total Records</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                        {reports.student_stats.total}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Present</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'Manrope' }}>
                        {reports.student_stats.present}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Absent</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#F59E0B]" style={{ fontFamily: 'Manrope' }}>
                        {reports.student_stats.absent}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Student Attendance Rate</CardTitle>
                    <CardDescription>Overall student attendance percentage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-4 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#10B981] transition-all duration-500"
                          style={{ width: `${reports.student_stats.percentage}%` }}
                        />
                      </div>
                      <div className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                        {reports.student_stats.percentage}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Teacher Stats */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Teacher Attendance Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Total Records</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                        {reports.teacher_stats.total}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Present</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'Manrope' }}>
                        {reports.teacher_stats.present}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium text-[#64748B]">Absent</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#F59E0B]" style={{ fontFamily: 'Manrope' }}>
                        {reports.teacher_stats.absent}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Teacher Attendance Rate</CardTitle>
                    <CardDescription>Overall teacher attendance percentage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-4 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#10B981] transition-all duration-500"
                          style={{ width: `${reports.teacher_stats.percentage}%` }}
                        />
                      </div>
                      <div className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                        {reports.teacher_stats.percentage}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
